/**
 * Configure dialog for a scheduled job (#1278 / #1280).
 *
 * Reached from the per-row Configure action on the catalog list view
 * AND from the Configuration tab of the detail page. Operators do NOT
 * create new jobs from this surface — every registered job is already
 * a row in Mongo (the boot seeder created it on first-seen). The
 * dialog edits an existing row.
 *
 * **Read-only header.** Job identity (display name, description,
 * jobId, max-duration, edit policy) is read-only — those fields are
 * code-owned and refreshed by the boot seeder on every deploy.
 *
 * **Editable fields:** cron picker (predefined cadences + "Custom"
 * with a 5-field expression validator), timezone picker (defaulting
 * to the operator's user.timezone), failure-alert routing, status
 * toggle.
 *
 * **`editPolicy === 'code-owned-schedule'` rows** render the cron +
 * timezone fields as read-only with a "Code-owned" badge — only
 * status and alerts are operator-editable on those rows.
 *
 * **Plain-language sweep.** No "cron expression" in operator copy
 * outside the developer-recognizable "Custom" path; defaults to "When
 * to run" + plain English cadence labels.
 */
import { useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
} from '@/components/ui'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { useToast } from '@/hooks/useToast'
import {
  adminScheduledJobsAPI,
  type ScheduledJobFailureAlert,
  type ScheduledJobRow,
  type ScheduledJobStatus,
} from '@/lib/api'

interface Props {
  jobId: string
  open: boolean
  onClose: () => void
  onSaved: (updated: ScheduledJobRow) => void
}

/** Predefined cadence presets the picker offers. The label is plain-
 * language; the value is the literal cron string the picker writes.
 * "Daily at 02:00" reads as 02:00 IN THE OPERATOR'S SELECTED TIMEZONE
 * — the explanation under the picker calls that out. */
const CADENCE_PRESETS: { label: string; cron: string }[] = [
  { label: 'Every 6 hours', cron: '0 */6 * * *' },
  { label: 'Every hour', cron: '0 * * * *' },
  { label: 'Daily at 02:00', cron: '0 2 * * *' },
  { label: 'Daily at 22:00', cron: '0 22 * * *' },
  { label: 'Weekly · Sundays 02:00', cron: '0 2 * * 0' },
]

/** A 5-field cron validator that only catches obvious shape errors.
 * The server runs the authoritative validator (cron@4.4.0 via
 * cronResolver.validateCron); this client-side check is for fast
 * feedback on the picker before submit. */
function validateCronShape(cron: string): { valid: boolean; error?: string } {
  const trimmed = cron.trim()
  if (!trimmed) return { valid: false, error: 'Please enter a 5-field cron expression.' }
  const fields = trimmed.split(/\s+/)
  if (fields.length !== 5) {
    return {
      valid: false,
      error: `A cron expression has exactly 5 fields (minute, hour, day-of-month, month, day-of-week). Got ${fields.length}.`,
    }
  }
  // Field chars: digits, *, /, -, ,
  const allowed = /^[\d*/,\-]+$/
  for (let i = 0; i < fields.length; i++) {
    if (!allowed.test(fields[i])) {
      return {
        valid: false,
        error: `Field ${i + 1} contains an unexpected character. Use digits, *, /, -, or , — for anything fancier, please test with a cron checker first.`,
      }
    }
  }
  return { valid: true }
}

export function ScheduledJobConfigureDialog({ jobId, open, onClose, onSaved }: Props) {
  const userTz = useUserTimezone()
  const toast = useToast()
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [job, setJob] = useState<ScheduledJobRow | null>(null)

  // Form state.
  const [cron, setCron] = useState('')
  const [timezone, setTimezone] = useState('')
  const [status, setStatus] = useState<ScheduledJobStatus>('active')
  const [onFailure, setOnFailure] = useState<ScheduledJobFailureAlert>('silent')
  const [usingCustomCron, setUsingCustomCron] = useState(false)

  const [cronError, setCronError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setLoadError(null)
    adminScheduledJobsAPI
      .get(jobId)
      .then((res) => {
        if (!alive) return
        setJob(res.job)
        setCron(res.job.cron)
        setTimezone(res.job.timezone)
        setStatus(res.job.status)
        setOnFailure(res.job.onFailure)
        setUsingCustomCron(!CADENCE_PRESETS.some((p) => p.cron === res.job.cron))
      })
      .catch((e: unknown) => {
        if (!alive) return
        setLoadError(e instanceof Error ? e.message : 'Could not load this scheduled job.')
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [jobId])

  function handleCronChange(value: string) {
    setCron(value)
    const v = validateCronShape(value)
    setCronError(v.valid ? null : v.error ?? null)
  }

  async function handleSave() {
    if (!job) return
    setSaveError(null)

    const patch: { cron?: string; timezone?: string; status?: ScheduledJobStatus; onFailure?: ScheduledJobFailureAlert } = {}
    if (cron !== job.cron) patch.cron = cron
    if (timezone !== job.timezone) patch.timezone = timezone
    if (status !== job.status) patch.status = status
    if (onFailure !== job.onFailure) patch.onFailure = onFailure

    if (Object.keys(patch).length === 0) {
      onClose()
      return
    }
    if (patch.cron !== undefined) {
      const v = validateCronShape(patch.cron)
      if (!v.valid) {
        setCronError(v.error ?? null)
        return
      }
    }

    setSaving(true)
    try {
      const updated = await adminScheduledJobsAPI.patch(job.jobId, patch)
      onSaved(updated)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Save failed.'
      setSaveError(message)
      toast({ variant: 'error', title: 'Could not save changes', description: message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{job ? `Configure: ${job.displayName}` : 'Configure scheduled job'}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {loadError && (
            <p role="alert" className="text-sm text-destructive">
              {loadError}
            </p>
          )}
          {!loading && !loadError && job && (
            <div className="space-y-5">
              {/* Read-only header — code-owned identity. */}
              <div className="rounded-md border bg-muted/30 p-3 text-xs">
                <div>
                  <span className="text-muted-foreground">Job id:</span>{' '}
                  <code className="text-foreground">{job.jobId}</code>
                </div>
                <div className="text-muted-foreground mt-1">{job.description}</div>
                <div className="mt-2 flex gap-3 text-muted-foreground">
                  <span>
                    Max duration: <strong>{job.maxDurationSec}s</strong>
                  </span>
                  <span>
                    Edit policy:{' '}
                    <Badge variant="secondary" className="text-[10px]">
                      {job.editPolicy === 'code-owned-schedule' ? 'Code-owned' : 'Editable'}
                    </Badge>
                  </span>
                </div>
              </div>

              <ScheduleFields
                editable={job.editPolicy === 'schedule-editable'}
                cron={cron}
                onCronChange={handleCronChange}
                cronError={cronError}
                usingCustomCron={usingCustomCron}
                onUsingCustomChange={setUsingCustomCron}
                timezone={timezone}
                onTimezoneChange={setTimezone}
                userTimezone={userTz}
              />

              <div>
                <Label htmlFor="sjcd-status">Status</Label>
                <Select
                  id="sjcd-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as ScheduledJobStatus)}
                  options={[
                    { value: 'active', label: 'Active — runs on the configured schedule' },
                    { value: 'paused', label: 'Paused — hold cadence, ready to resume' },
                    { value: 'disabled', label: 'Disabled — stop and clear cadence' },
                  ]}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Pausing preserves the next-run time so resuming continues the same cadence;
                  disabling clears it (resuming after a disable picks the next run from now).
                </p>
              </div>

              <div>
                <Label htmlFor="sjcd-alert">On failure</Label>
                <Select
                  id="sjcd-alert"
                  value={onFailure}
                  onChange={(e) => setOnFailure(e.target.value as ScheduledJobFailureAlert)}
                  options={[
                    { value: 'silent', label: 'Silent (only the run record carries the failure)' },
                    { value: 'email', label: 'Email someone' },
                    { value: 'slack', label: 'Post to a Slack channel' },
                  ]}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Failure routing — where the runner sends the alert when a run errors or runs over
                  the max duration. (Transport wiring beyond logging lands in a follow-up.)
                </p>
              </div>

              {saveError && (
                <p role="alert" className="text-sm text-destructive">
                  {saveError}
                </p>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading || !job || Boolean(cronError)}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface ScheduleFieldsProps {
  editable: boolean
  cron: string
  onCronChange: (value: string) => void
  cronError: string | null
  usingCustomCron: boolean
  onUsingCustomChange: (value: boolean) => void
  timezone: string
  onTimezoneChange: (value: string) => void
  userTimezone: string
}

function ScheduleFields({
  editable,
  cron,
  onCronChange,
  cronError,
  usingCustomCron,
  onUsingCustomChange,
  timezone,
  onTimezoneChange,
  userTimezone,
}: ScheduleFieldsProps) {
  if (!editable) {
    return (
      <div className="rounded-md border bg-muted/30 p-3 text-xs">
        <div className="text-muted-foreground">
          <strong>This job's schedule is code-owned.</strong> The cadence and time zone are pinned
          to the values in the source code; you can still change the status and alerts above.
        </div>
        <div className="mt-2 font-mono text-foreground">{cron}</div>
        <div className="text-muted-foreground">in {timezone}</div>
      </div>
    )
  }

  return (
    <>
      <div>
        <Label htmlFor="sjcd-cron-preset">When to run</Label>
        <Select
          id="sjcd-cron-preset"
          value={usingCustomCron ? '__custom__' : cron}
          onChange={(e) => {
            const v = e.target.value
            if (v === '__custom__') {
              onUsingCustomChange(true)
            } else {
              onUsingCustomChange(false)
              onCronChange(v)
            }
          }}
          options={[
            ...CADENCE_PRESETS.map((p) => ({ value: p.cron, label: p.label })),
            { value: '__custom__', label: 'Custom (5-field cron expression)…' },
          ]}
        />
        {usingCustomCron && (
          <div className="mt-2">
            <Input
              value={cron}
              onChange={(e) => onCronChange(e.target.value)}
              placeholder="e.g. 0 */4 * * *"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Five fields, space-separated: minute, hour, day-of-month, month, day-of-week. Read in
              the time zone selected below.
            </p>
          </div>
        )}
        {cronError && (
          <p role="alert" className="text-xs text-destructive mt-1">
            {cronError}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="sjcd-tz">Time zone</Label>
        <Input
          id="sjcd-tz"
          value={timezone}
          onChange={(e) => onTimezoneChange(e.target.value)}
          placeholder="e.g. America/New_York"
        />
        <p className="text-xs text-muted-foreground mt-1">
          The schedule is read in this time zone. Defaults to your preference ({userTimezone}) when
          first created — change here if this job should follow a different zone (e.g. for nightly
          jobs that should anchor to UTC for cross-region consistency).
        </p>
      </div>
    </>
  )
}
